import * as fs from "node:fs";
import * as path from "node:path";
import {
  decodeSkaileManifest,
  type AssetEntry as ManifestAssetEntry,
  SKAILE_MANIFEST_FILENAME,
  type SkPublishManifest,
} from "@skaile/workspaces/core/publish-manifest";
import * as yaml from "js-yaml";
import * as z from "zod";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Semver regex matching `MAJOR.MINOR.PATCH` with optional `-<prerelease>` and
 * `+<build>` segments. Author-shipped inventory entries must declare a version
 * that matches this regex.
 *
 * @docLink packages/discovery/concepts#author-manifest-semver
 */
export const SEMVER_RE = /^\d+\.\d+\.\d+(?:-[a-zA-Z0-9.-]+)?(?:\+[a-zA-Z0-9.-]+)?$/;

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const PublisherOverrideSchema = z.object({
  /** Relative path within the repo (trailing slash convention). */
  path: z.string().min(1),
  /** Publisher namespace override for assets under this path. */
  publisher: z
    .string()
    .regex(/^@[\w-]+$/, "Publisher must start with @ and contain only word chars / hyphens"),
});

const SyncConfigSchema = z
  .object({
    /** How the catalog backend learns about repo changes. */
    trigger: z.enum(["webhook", "poll"]).default("webhook"),
    /** Seconds between polls. Required when trigger=poll. */
    poll_interval: z.number().int().positive().optional(),
  })
  .refine((data) => data.trigger !== "poll" || data.poll_interval !== undefined, {
    message: "poll_interval is required when trigger is poll",
    path: ["poll_interval"],
  });

/**
 * Single entry in the author-shipped inventory (the `assets:` block of a
 * `version: 2` source config — adapted from `skaile.manifest.yaml` via
 * {@link manifestToSourceConfig}).
 *
 * When the `assets:` block is present, discovery uses these entries as the
 * source of truth (`discoverFromManifest`) instead of running glob walk +
 * publisher resolution.
 *
 * @docLink packages/discovery/concepts#author-manifest-asset-entry
 */
const AssetEntrySchema = z
  .object({
    /** Asset root directory, relative to the repo. Files are resolved under it. */
    path: z.string().min(1),
    /**
     * Optional recursive root. When set (and `files` absent), the asset spans
     * every file under `<path>` (walked recursively). Mirrors the
     * `skaile.manifest.yaml` `root:` form via `manifestToSourceConfig`.
     */
    root: z.string().min(1).optional(),
    /** Asset kind — validated against `IAssetKindRegistry` at discovery time. */
    kind: z.string().min(1),
    /** Publisher namespace. Required per entry; overrides publisher_default / _overrides. */
    publisher: z
      .string()
      .regex(/^@[\w-]+$/, "Publisher must start with @ and contain only word chars / hyphens"),
    /** Asset name. Identity tuple is `<publisher>/<name>@<version>`. */
    name: z.string().min(1),
    /** Semver version string. */
    version: z.string().regex(SEMVER_RE, "Version must be semver MAJOR.MINOR.PATCH"),
    /**
     * Hex-encoded SHA256 hash of the deterministic tarball contents.
     * Optional on write, REQUIRED on Catalog publish (Phase 4).
     */
    sha256: z
      .string()
      .regex(/^[a-f0-9]{64}$/, "sha256 must be 64-character hex")
      .optional(),
    /**
     * Curated file filter (paths relative to `path:`). At least one entry when
     * present. Optional: a `root`-only entry omits it and is materialised by a
     * recursive walk at discovery time.
     */
    files: z.array(z.string().min(1)).min(1).optional(),
    /** Author-declared requires-graph entries. */
    requires: z.array(z.string().min(1)).default([]),
    /** Optional passthrough to `AssetDefinition.manifest`. */
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((a) => a.root !== undefined || a.files !== undefined, {
    message: "asset entry must declare either `root` or `files`",
    path: ["files"],
  });

/**
 * Root schema for discovery's **internal** source config — the IR the pipeline
 * consumes. On disk a repo declares its inventory in `skaile.manifest.yaml`,
 * which {@link manifestToSourceConfig} adapts into this shape; the raw schema is
 * also accepted directly (tests, programmatic callers).
 *
 * Declares how a repo maps to Catalog publisher namespaces.
 * Template variable `<domain>` in publisher_default is replaced at discovery
 * time with the first-level directory name containing the asset.
 *
 * Schema version `1` is the original glob-discovery surface. Version `2`
 * extends the schema with an optional `assets:` block — when present,
 * discovery uses the author-shipped inventory as authoritative and skips
 * glob walk + publisher resolution + AI enrichment.
 *
 * @docLink packages/discovery/concepts#source-config-schema
 */
export const SourceConfigSchema = z
  .object({
    /** Schema version. `1` (glob mode) or `2` (allows `assets:` block). */
    version: z.union([z.literal(1), z.literal(2)]),

    /**
     * Default publisher namespace template.
     * Use `@<domain>` to derive from first-level directory name.
     * Use a literal like `@skaile` if the entire repo maps to one publisher.
     */
    publisher_default: z
      .string()
      .min(1)
      .refine((v) => v.startsWith("@"), "publisher_default must start with @"),

    /** Default git ref for version resolution. */
    default_ref: z.string().min(1).default("main"),

    /** Per-path publisher overrides. Evaluated in order; first match wins. */
    publisher_overrides: z.array(PublisherOverrideSchema).default([]),

    /**
     * Repo-relative path prefixes holding assets meant only for developing the
     * project itself (e.g. `ai-assets-dev/`). Excluded from a normal discovery
     * run; re-included only when discovery is invoked with `includeDev` (the
     * `--dev` flag on `skaile source sync` / `source add`). Conventionally a
     * single top-level folder. See `docs/sections/discovery/dev-assets.md`.
     */
    dev_paths: z.array(z.string().min(1)).default([]),

    /** Optional sync configuration for the catalog backend. */
    sync: SyncConfigSchema.optional(),

    /**
     * Optional author-shipped asset inventory. When present, discovery uses
     * these entries verbatim and skips glob walk + AI enrichment.
     *
     * Requires `version: 2`.
     */
    assets: z.array(AssetEntrySchema).optional(),
  })
  .refine((data) => !(data.assets && data.version !== 2), {
    message: "`assets:` block requires version 2",
    path: ["version"],
  });

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Validated internal source config (adapted from `skaile.manifest.yaml`).
 *
 * @docLink packages/discovery/concepts#source-config
 */
export type SourceConfig = z.infer<typeof SourceConfigSchema>;

/**
 * A `SourceConfig` known to be `version: 2` with the `assets:` block populated.
 * Returned from {@link isManifestMode} after a successful narrow.
 *
 * @docLink packages/discovery/concepts#source-config-v2
 */
export type SourceConfigV2 = SourceConfig & { version: 2; assets: AssetEntry[] };

/**
 * A single entry in the author-shipped inventory.
 *
 * @docLink packages/discovery/concepts#author-manifest-asset-entry
 */
export type AssetEntry = z.infer<typeof AssetEntrySchema>;

/**
 * A single per-path publisher namespace override entry.
 *
 * The first matching override (by path prefix) wins during publisher resolution.
 *
 * @docLink packages/discovery/concepts#publisher-override
 */
export type PublisherOverride = z.infer<typeof PublisherOverrideSchema>;

/**
 * Sync configuration that controls how the catalog backend learns about repo changes.
 *
 * @docLink packages/discovery/concepts#sync-config
 */
export type SyncConfig = z.infer<typeof SyncConfigSchema>;

// ---------------------------------------------------------------------------
// Parser / Validator
// ---------------------------------------------------------------------------

/**
 * Successful result from {@link validateSourceConfig} or {@link loadSourceConfig}.
 *
 * `warnings` lists non-fatal anomalies (e.g. `assets:` present alongside
 * `publisher_overrides:` — the latter is ignored in manifest mode).
 *
 * @docLink packages/discovery/concepts#validation-success
 */
export interface ValidationSuccess {
  ok: true;
  config: SourceConfig;
  warnings?: string[];
}

/**
 * Failed result from {@link validateSourceConfig} or {@link loadSourceConfig},
 * carrying a list of per-field validation errors.
 *
 * @docLink packages/discovery/concepts#validation-failure
 */
export interface ValidationFailure {
  ok: false;
  errors: Array<{ path: string; message: string }>;
}

/**
 * Discriminated union returned by {@link validateSourceConfig} and {@link loadSourceConfig}.
 *
 * Use `result.ok` to narrow to {@link ValidationSuccess} or {@link ValidationFailure}.
 *
 * @docLink packages/discovery/concepts#validation-result
 */
export type ValidationResult = ValidationSuccess | ValidationFailure;

/**
 * Parse raw YAML text into a plain object.
 *
 * Throws if the YAML is syntactically invalid. Pass the result to
 * {@link validateSourceConfig} to apply schema validation.
 *
 * @param yamlText - Raw YAML content of an internal source config
 * @returns The parsed object (unvalidated)
 * @docLink packages/discovery/concepts#parse-source-config
 */
export function parseSourceConfig(yamlText: string): unknown {
  return yaml.load(yamlText);
}

/**
 * Validate a parsed object against the {@link SourceConfigSchema}.
 *
 * @param parsed - Plain object produced by {@link parseSourceConfig} or any other YAML parser
 * @returns A {@link ValidationSuccess} with the typed config, or a {@link ValidationFailure} with field errors
 * @docLink packages/discovery/concepts#validate-source-config
 */
export function validateSourceConfig(parsed: unknown): ValidationResult {
  const result = SourceConfigSchema.safeParse(parsed);
  if (result.success) {
    const warnings = collectWarnings(result.data);
    return warnings.length > 0
      ? { ok: true, config: result.data, warnings }
      : { ok: true, config: result.data };
  }
  return {
    ok: false,
    errors: result.error.issues.map((issue) => ({
      path: issue.path.join("."),
      message: issue.message,
    })),
  };
}

/**
 * Convenience wrapper that parses YAML and validates in a single call.
 *
 * Equivalent to calling {@link parseSourceConfig} followed by {@link validateSourceConfig}.
 *
 * @param yamlText - Raw YAML content of an internal source config
 * @returns A {@link ValidationResult} (success or failure)
 * @docLink packages/discovery/concepts#load-source-config
 */
export function loadSourceConfig(yamlText: string): ValidationResult {
  const parsed = parseSourceConfig(yamlText);
  return validateSourceConfig(parsed);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Narrow a {@link SourceConfig} to {@link SourceConfigV2} when it carries an
 * author-shipped inventory.
 *
 * @param config - Validated source config
 * @returns `true` when `version === 2` and `assets:` is present (and non-empty
 *   array — empty arrays still trigger manifest mode and yield an empty
 *   discovery result, which matches an empty repo).
 * @docLink packages/discovery/concepts#is-manifest-mode
 */
export function isManifestMode(config: SourceConfig): config is SourceConfigV2 {
  return config.version === 2 && Array.isArray(config.assets);
}

/**
 * Test whether a repo-relative path falls under one of the configured
 * `dev_paths` prefixes.
 *
 * Matching is whole-segment: a `dev_paths` entry of `ai-assets-dev` matches
 * `ai-assets-dev` itself and anything under `ai-assets-dev/…`, but not a
 * sibling like `ai-assets-development`. Trailing slashes on either side are
 * normalized away.
 *
 * @param relativePath - Path relative to the source root (POSIX separators)
 * @param devPaths - The `dev_paths` list from a {@link SourceConfig}
 * @returns `true` when discovery should exclude the path in a normal run
 * @docLink packages/discovery/concepts#dev-paths
 */
export function isExcludedDevPath(relativePath: string, devPaths: readonly string[]): boolean {
  if (devPaths.length === 0) return false;
  const rel = relativePath.replace(/^\.\//, "").replace(/\/+$/, "");
  for (const raw of devPaths) {
    const dp = raw.replace(/^\.\//, "").replace(/\/+$/, "");
    if (dp.length === 0) continue;
    if (rel === dp || rel.startsWith(`${dp}/`)) return true;
  }
  return false;
}

function collectWarnings(config: SourceConfig): string[] {
  const warnings: string[] = [];
  if (Array.isArray(config.assets) && config.publisher_overrides.length > 0) {
    warnings.push(
      "`assets:` block is present; `publisher_overrides:` will be ignored in manifest mode.",
    );
  }
  return warnings;
}

// ---------------------------------------------------------------------------
// skaile.manifest.yaml adapter
// ---------------------------------------------------------------------------

/**
 * Strip a single leading `@` scope sigil from a publisher namespace.
 *
 * The internal {@link SourceConfig} carries `@`-prefixed publishers
 * (`publisher_default`, `publisher_overrides[].publisher`) — that is correct at
 * the config layer. But `DiscoveredAsset.publisher` (and every downstream ref)
 * must be **bare** (`core/src/ref.ts`: "publisher never carries a leading `@`");
 * the scope sigil is re-added by the ref formatter. Emitting `@acme` here yields
 * `@@acme` in display and un-addable refs. Shared by both discovery entry points
 * (`discover.ts` glob walk and `tree-entries.ts` virtual tree).
 */
export function barePublisher(publisher: string): string {
  return publisher.startsWith("@") ? publisher.slice(1) : publisher;
}

/** Ensure a publisher string carries the leading `@` scope sigil. */
function atPublisher(publisher: string): string {
  return publisher.startsWith("@") ? publisher : `@${publisher}`;
}

/** Derive `@<first-segment>` from a path; `@unknown` for a bare/dot root. */
function derivePublisherFromPath(p: string): string {
  const seg = p.split("/").find((s) => s.length > 0 && s !== ".");
  return seg ? `@${seg}` : "@unknown";
}

/** Flatten a manifest `files:` (string globs or granular `{path}`) to strings. */
function manifestFilesToStrings(files: ManifestAssetEntry["files"]): string[] | undefined {
  if (!Array.isArray(files) || files.length === 0) return undefined;
  const out = files.map((f) => (typeof f === "string" ? f : f.path)).filter((p) => p.length > 0);
  return out.length > 0 ? out : undefined;
}

/** Collect store-listing metadata from a manifest entry (manifest repo-level fallback). */
function manifestEntryMetadata(
  a: ManifestAssetEntry,
  manifest: SkPublishManifest,
): Record<string, unknown> | undefined {
  const meta: Record<string, unknown> = {};
  if (a.description !== undefined) meta.description = a.description;
  if (a.keywords !== undefined) meta.keywords = a.keywords;
  if (a.category !== undefined) meta.category = a.category;
  const license = a.license ?? manifest.license;
  if (license !== undefined) meta.license = license;
  const homepage = a.homepage ?? manifest.homepage;
  if (homepage !== undefined) meta.homepage = homepage;
  return Object.keys(meta).length > 0 ? meta : undefined;
}

/** Map one `skaile.manifest.yaml` asset entry to the internal {@link AssetEntry}. */
function adaptManifestAssetEntry(a: ManifestAssetEntry, manifest: SkPublishManifest): AssetEntry {
  const root = typeof a.root === "string" && a.root.length > 0 ? a.root : undefined;
  // `root` → recursive walk (files materialised at discovery time). Explicit
  // `files` (no root) → path-anchored at the repo root ("."); discovery joins
  // them under the asset root unchanged.
  const assetPath = root ?? ".";
  const files = root ? undefined : manifestFilesToStrings(a.files);

  const publisher = a.publisher
    ? atPublisher(a.publisher)
    : manifest.publisher
      ? atPublisher(manifest.publisher)
      : derivePublisherFromPath(assetPath);

  const entry: AssetEntry = {
    path: assetPath,
    kind: a.kind,
    publisher,
    name: a.name,
    version: a.version ?? manifest.version ?? "0.1.0",
    requires: a.dependencies ?? [],
  };
  if (root) entry.root = root;
  if (files) entry.files = files;
  if (a.sha256) entry.sha256 = a.sha256;
  const metadata = manifestEntryMetadata(a, manifest);
  if (metadata) entry.metadata = metadata;
  return entry;
}

/**
 * Adapt a decoded `skaile.manifest.yaml` ({@link SkPublishManifest}) into the
 * internal {@link SourceConfig} the discovery pipeline consumes.
 *
 * - An inventory (`assets:`) maps to a `version: 2` manifest-mode config, one
 *   {@link AssetEntry} per manifest asset (publisher/version inherited from the
 *   repo-level manifest when the entry omits them; `dependencies` → `requires`;
 *   store-listing fields → `metadata`).
 * - No inventory maps to a `version: 1` glob-mode config carrying the derived
 *   `publisher_default` (`@<publisher>` when declared, else `@<domain>`).
 *
 * The manifest carries no `publisher_default` template / `publisher_overrides` /
 * `sync` — those were `.skaile-source.yaml`-only and are synthesised/defaulted.
 *
 * @docLink packages/discovery/concepts#manifest-to-source-config
 */
export function manifestToSourceConfig(manifest: SkPublishManifest): SourceConfig {
  const publisherDefault = manifest.publisher ? atPublisher(manifest.publisher) : "@<domain>";
  const devPaths = manifest.dev_paths ?? [];

  if (!manifest.assets || manifest.assets.length === 0) {
    return {
      version: 1,
      publisher_default: publisherDefault,
      default_ref: "main",
      publisher_overrides: [],
      dev_paths: devPaths,
    };
  }

  return {
    version: 2,
    publisher_default: publisherDefault,
    default_ref: "main",
    publisher_overrides: [],
    dev_paths: devPaths,
    assets: manifest.assets.map((a) => adaptManifestAssetEntry(a, manifest)),
  };
}

/**
 * Decode `skaile.manifest.yaml` text and adapt it to an internal
 * {@link SourceConfig}. Fatal decode diagnostics (YAML syntax / non-object
 * root) surface as a {@link ValidationFailure}; otherwise an adapted
 * {@link SourceConfig} is returned.
 *
 * @docLink packages/discovery/concepts#decode-manifest-source-config
 */
export function decodeManifestSourceConfig(text: string): ValidationResult {
  const { manifest, diagnostics } = decodeSkaileManifest(text);
  const fatal = diagnostics.filter((d) => d.severity === "error");
  if (fatal.length > 0) {
    return {
      ok: false,
      errors: fatal.map((d) => ({ path: d.path ?? "", message: d.message })),
    };
  }
  return { ok: true, config: manifestToSourceConfig(manifest) };
}

/**
 * Read `<dir>/skaile.manifest.yaml` (if present) and adapt it to a
 * {@link SourceConfig}. Returns `undefined` when the file is absent or
 * unparseable — the same tolerant contract the glob path expects.
 *
 * @docLink packages/discovery/concepts#read-manifest-source-config
 */
export function readManifestSourceConfig(dir: string): SourceConfig | undefined {
  const filePath = path.join(dir, SKAILE_MANIFEST_FILENAME);
  if (!fs.existsSync(filePath)) return undefined;
  try {
    const result = decodeManifestSourceConfig(fs.readFileSync(filePath, "utf-8"));
    return result.ok ? result.config : undefined;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Sidecar overlay (PR-3)
// ---------------------------------------------------------------------------

/**
 * A {@link SourceConfig} produced by merging a base in-repo config with a
 * local sidecar overlay. Always shaped like a regular `SourceConfig` —
 * downstream consumers treat it identically.
 *
 * @docLink packages/discovery/concepts#merged-source-config
 */
export type MergedSourceConfig = SourceConfig;

/**
 * Merge a base in-repo source config with a local sidecar overlay (both
 * adapted from their respective `skaile.manifest.yaml`).
 *
 * Merge rules (per PR-3 §D.3):
 *
 * | Field                 | Resolution                                          |
 * |-----------------------|-----------------------------------------------------|
 * | `version`             | sidecar wins (sidecar is always v2)                 |
 * | `publisher_default`   | sidecar wins if set, else base                      |
 * | `publisher_overrides` | sidecar wins if non-empty, else base                |
 * | `default_ref`         | sidecar wins if set, else base                      |
 * | `sync`                | sidecar wins if set, else base                      |
 * | `assets:`             | sidecar fully **replaces** base (no partial merge)  |
 *
 * Pure function — no I/O. Callers responsible for loading both configs.
 *
 * @param base - The in-repo source config (or `undefined` if absent)
 * @param overlay - The sidecar source config (or `undefined` for identity)
 * @returns The merged config, or `undefined` if both inputs are missing
 * @docLink packages/discovery/concepts#merge-source-configs
 */
export function mergeSourceConfigs(
  base: SourceConfig | undefined,
  overlay: SourceConfig | undefined,
): MergedSourceConfig | undefined {
  if (!base && !overlay) return undefined;
  if (!overlay) return base;
  if (!base) return overlay;

  // Start from base, layer overlay fields per rules above.
  const merged: SourceConfig = { ...base };

  // version: sidecar always wins (sidecars are minted at v2).
  merged.version = overlay.version;

  // publisher_default: sidecar wins iff set (and non-empty). Schema requires
  // non-empty so any present overlay value wins.
  if (overlay.publisher_default && overlay.publisher_default.length > 0) {
    merged.publisher_default = overlay.publisher_default;
  }

  // publisher_overrides: sidecar wins iff non-empty.
  if (Array.isArray(overlay.publisher_overrides) && overlay.publisher_overrides.length > 0) {
    merged.publisher_overrides = overlay.publisher_overrides;
  }

  // dev_paths: sidecar wins iff non-empty.
  if (Array.isArray(overlay.dev_paths) && overlay.dev_paths.length > 0) {
    merged.dev_paths = overlay.dev_paths;
  }

  // default_ref: sidecar wins iff set. Schema gives default "main" so
  // we treat a non-default overlay value as a signal of intent.
  if (overlay.default_ref && overlay.default_ref.length > 0) {
    merged.default_ref = overlay.default_ref;
  }

  // sync: sidecar wins iff set.
  if (overlay.sync !== undefined) {
    merged.sync = overlay.sync;
  }

  // assets: sidecar fully replaces base on presence.
  if (overlay.assets !== undefined) {
    merged.assets = overlay.assets;
  }

  return merged;
}

/**
 * Load and merge a base `skaile.manifest.yaml` (at `<rootPath>/skaile.manifest.yaml`)
 * with an optional sidecar manifest at `<sidecarPath>/skaile.manifest.yaml`.
 *
 * Either side may be absent. The result is wrapped in a {@link ValidationResult}
 * so the discovery dispatcher can surface per-side parse errors uniformly.
 *
 * Field merge follows {@link mergeSourceConfigs}. The sidecar's
 * `skaile.manifest.yaml` is the only sidecar artifact this helper touches; the
 * README + lock file under `<sidecarPath>` are consumer concerns.
 *
 * @param rootPath - Absolute path to the upstream repo root
 * @param sidecarPath - Optional absolute path to the sidecar directory
 *   (e.g. `~/.skaile/sources/<slug>/`)
 * @returns A {@link ValidationResult} carrying the merged config. Returns
 *   `{ ok: true, config: <merged> }` when at least one side parses; returns
 *   `{ ok: false, errors: [...] }` only when a present file is structurally
 *   invalid. Missing files on both sides yield a typed success with an
 *   empty/default v2 config.
 * @docLink packages/discovery/concepts#load-merged-source-config
 */
export function loadMergedSourceConfig(rootPath: string, sidecarPath?: string): ValidationResult {
  // The dir-based overlay is a thin adapter over the file-based variant: the
  // sidecar's overlay always lives at `<sidecarPath>/skaile.manifest.yaml`.
  return loadMergedSourceConfigFromFile(
    rootPath,
    sidecarPath ? path.join(sidecarPath, SKAILE_MANIFEST_FILENAME) : undefined,
  );
}

/**
 * File-based twin of {@link loadMergedSourceConfig}: merge the base
 * `<rootPath>/skaile.manifest.yaml` with an overlay read from an EXPLICIT file
 * path rather than a `<dir>/skaile.manifest.yaml` convention.
 *
 * The local store keeps curated overlays as a flat `<slug>.yaml` (not a
 * `<dir>/skaile.manifest.yaml`), so `source add`/`sync` route through this
 * variant, pointing `overlayManifestFile` straight at
 * `~/.skaile/store/manifests/<slug>.yaml`.
 *
 * @param rootPath - Absolute path to the upstream repo root
 * @param overlayManifestFile - Optional absolute path to the overlay manifest file
 * @returns Same {@link ValidationResult} contract as {@link loadMergedSourceConfig}
 * @docLink packages/discovery/concepts#load-merged-source-config
 */
export function loadMergedSourceConfigFromFile(
  rootPath: string,
  overlayManifestFile?: string,
): ValidationResult {
  const baseResult = loadSourceConfigFromDir(rootPath);
  if (baseResult && !baseResult.ok) return baseResult;
  const base = baseResult?.ok ? baseResult.config : undefined;

  let overlay: SourceConfig | undefined;
  const overlayWarnings: string[] = [];
  if (overlayManifestFile) {
    const overlayResult = loadSourceConfigFromFile(overlayManifestFile);
    if (overlayResult && !overlayResult.ok) return overlayResult;
    if (overlayResult?.ok) {
      overlay = overlayResult.config;
      if (overlayResult.warnings) overlayWarnings.push(...overlayResult.warnings);
    }
  }

  const merged = mergeSourceConfigs(base, overlay);
  if (!merged) {
    // No file on either side — return a typed empty v2 default so callers
    // don't have to branch on undefined.
    return {
      ok: true,
      config: {
        version: 2,
        publisher_default: "@<domain>",
        default_ref: "main",
        publisher_overrides: [],
        dev_paths: [],
      },
    };
  }

  const warnings = collectWarnings(merged);
  if (overlayWarnings.length > 0) warnings.push(...overlayWarnings);
  return warnings.length > 0
    ? { ok: true, config: merged, warnings }
    : { ok: true, config: merged };
}

/**
 * Read `<dir>/skaile.manifest.yaml` if present and adapt it to a
 * {@link SourceConfig}. Returns `undefined` when the file is missing (consumers
 * treat as absent), and a {@link ValidationFailure} when the file exists but is
 * structurally invalid.
 *
 * Internal helper for {@link loadMergedSourceConfig}.
 */
function loadSourceConfigFromDir(dir: string): ValidationResult | undefined {
  return loadSourceConfigFromFile(path.join(dir, SKAILE_MANIFEST_FILENAME));
}

/**
 * Read an explicit manifest file path and adapt it to a {@link SourceConfig}.
 * Returns `undefined` when the file is missing (consumers treat as absent), a
 * {@link ValidationFailure} when it exists but is structurally invalid.
 *
 * Internal helper shared by {@link loadMergedSourceConfig} (dir convention) and
 * {@link loadMergedSourceConfigFromFile} (explicit path — the store overlay).
 */
function loadSourceConfigFromFile(filePath: string): ValidationResult | undefined {
  if (!fs.existsSync(filePath)) return undefined;
  try {
    const text = fs.readFileSync(filePath, "utf-8");
    return decodeManifestSourceConfig(text);
  } catch (err) {
    return {
      ok: false,
      errors: [
        {
          path: filePath,
          message: err instanceof Error ? err.message : String(err),
        },
      ],
    };
  }
}
